<template>
  <div>
    <AppNavbar />
    <div class="container mt-5">
      <!--
        SPEC_NOTAS_DE_CREDITO_v0.1.md · D44 · RN-138 a RN-144.

        Esta pantalla se llamaba «Devoluciones» y su historial leía la tabla
        `devoluciones` — CERO filas en producción el 2026-10-09, el mismo día en
        que se emitieron dos notas de crédito por anulación de cobro. El
        operador leía «Todavía no hay devoluciones registradas» con dos NC
        emitidas. Era D40 otra vez: el nombre y la fuente de otro producto.

        Ahora se llama por lo que muestra y lista las NC de los DOS orígenes. La
        devolución de mercadería sigue: es una forma de ORIGINAR una NC, y por
        eso es una pestaña de acá y no la pantalla entera.
      -->
      <AppHeader title="Notas de crédito">
        <template #buttons>
          <button type="button" class="btn btn-outline-secondary" :disabled="cargando"
                  data-test="nc-recargar" @click="recargarPestanaActiva()">
            <i class="bi bi-arrow-repeat me-1"></i>
            Recargar
          </button>
        </template>
      </AppHeader>

      <ul class="nav nav-pills mt-4 mb-3">
        <li class="nav-item">
          <button type="button" class="nav-link" :class="{ active: pestanaActiva === 'historial' }"
                  data-test="nc-pestana-historial" @click="cambiarPestana('historial')">
            Emitidas
          </button>
        </li>
        <!-- Originar una devolución exige `cobrar_venta`, como siempre. Ver y
             entregar una NC ya emitida no (RN-144): el cajero la ve con
             `ver_cobros`. -->
        <li v-if="puedeDevolver" class="nav-item">
          <button type="button" class="nav-link" :class="{ active: pestanaActiva === 'buscar' }"
                  data-test="nc-pestana-devolver" @click="cambiarPestana('buscar')">
            Devolver mercadería
          </button>
        </li>
      </ul>

      <!-- Pestaña: notas de crédito emitidas -->
      <div v-if="pestanaActiva === 'historial'" class="table-responsive card p-3" data-test="nc-historial">
        <div class="d-flex flex-wrap gap-2 align-items-end mb-3">
          <div class="flex-grow-1" style="min-width: 220px">
            <label class="form-label small mb-1" for="nc-busqueda">Buscar</label>
            <input id="nc-busqueda" v-model.trim="filtro" type="search" class="form-control"
                   placeholder="Número, cliente, documento o factura" data-test="nc-busqueda"
                   @input="buscarConDemora">
          </div>
          <div>
            <label class="form-label small mb-1" for="nc-origen">Origen</label>
            <select id="nc-origen" v-model="origen" class="form-select" data-test="nc-origen"
                    @change="cargarNotas(1)">
              <option value="">Todos</option>
              <option value="ANULACION_COBRO">Anulación de cobro</option>
              <option value="DEVOLUCION_MERCADERIA">Devolución de mercadería</option>
            </select>
          </div>
          <small class="text-muted ms-auto" data-test="nc-total">
            {{ notasTotal }} {{ notasTotal === 1 ? 'nota de crédito' : 'notas de crédito' }}
          </small>
        </div>

        <table class="table table-striped align-middle">
          <thead>
            <tr>
              <th>N.º nota de crédito</th>
              <th>Origen</th>
              <th>Cliente</th>
              <th>Anula la factura</th>
              <th class="text-end">Monto</th>
              <th>Fecha</th>
              <th class="text-end">Acción</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="cargando">
              <td colspan="7" class="text-center py-4">Cargando notas de crédito...</td>
            </tr>
            <tr v-else-if="!notas.length">
              <td colspan="7" class="text-center py-4" data-test="nc-vacio">
                {{ filtro || origen ? 'Ninguna nota de crédito coincide con la búsqueda.' : 'Todavía no se emitió ninguna nota de crédito.' }}
              </td>
            </tr>
            <tr v-else v-for="nc in notas" :key="nc.receipt_id" :data-test="`nc-fila-${nc.receipt_id}`">
              <td class="vx-num">{{ nc.numero || 'Número pendiente' }}</td>
              <td>
                <span class="badge" :class="nc.origen === 'ANULACION_COBRO' ? 'bg-secondary' : 'bg-info text-dark'">
                  {{ ORIGEN_ETIQUETA[nc.origen] || nc.origen }}
                </span>
                <div v-if="nc.cr_cobro_id" class="small text-muted">Cobro #{{ nc.cr_cobro_id }}</div>
              </td>
              <td>
                {{ nc.cliente_nombre || '—' }}
                <div v-if="nc.nro_documento" class="small text-muted">{{ nc.nro_documento }}</div>
              </td>
              <td class="vx-num">{{ nc.documento_acreditado || '—' }}</td>
              <td class="text-end">{{ formatearMonto(nc.monto_total) }}</td>
              <td>
                {{ formatearFecha(nc.emision_at) }}
                <div v-if="nc.motivo" class="small text-muted" :title="nc.motivo">{{ recortar(nc.motivo) }}</div>
              </td>
              <td class="text-end">
                <button type="button" class="btn btn-outline-primary btn-sm"
                        :data-test="`nc-comprobante-${nc.receipt_id}`" @click="ncAbierta = nc.receipt_id">
                  <i class="bi bi-box-arrow-up-right me-1"></i>
                  Entregar
                </button>
              </td>
            </tr>
          </tbody>
        </table>
        <AppPagination :currentPage="notasPagina" :totalPages="notasTotalPaginas"
                       @page-changed="cargarNotas" />
      </div>

      <!-- Pestaña: devolver mercadería (el flujo heredado, intacto) -->
      <div v-else-if="pestanaActiva === 'buscar' && puedeDevolver" class="table-responsive card p-3"
           data-test="nc-devolver">
        <div class="d-flex justify-content-between align-items-center mb-2">
          <h2 class="mb-0">Ventas</h2>
          <small class="text-muted">
            Página {{ ventasPagina }} de {{ ventasTotalPaginas }} · {{ ventasTotal }} registros
          </small>
        </div>
        <table class="table table-striped align-middle">
          <thead>
            <tr>
              <th>N° factura</th>
              <th>Cliente</th>
              <th>Fecha</th>
              <th class="text-end">Total</th>
              <th>Estado fiscal</th>
              <th>Acción</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="cargando">
              <td colspan="6" class="text-center py-4">Cargando ventas...</td>
            </tr>
            <tr v-else-if="!ventas.length">
              <td colspan="6" class="text-center py-4">No hay ventas para mostrar.</td>
            </tr>
            <tr v-else v-for="venta in ventas" :key="venta.id_venta">
              <td>{{ venta.nro_factura || '—' }}</td>
              <td>{{ venta.nombre_razon_social || '—' }}</td>
              <td>{{ formatearFecha(venta.fecha_emision) }}</td>
              <td class="text-end">{{ formatearMonto(venta.total_iva_incluido || venta.total_sin_iva) }}</td>
              <td>
                <span :class="['badge', fiscalBadgeClass(venta.fiscal_status)]">
                  {{ (venta.fiscal_status || '—').toUpperCase() }}
                </span>
              </td>
              <td>
                <button v-if="esDevolvible(venta)" type="button" class="btn btn-outline-warning btn-sm"
                        :disabled="abriendoDevolucion === venta.id_venta"
                        @click="abrirDevolucion(venta.id_venta)">
                  <span v-if="abriendoDevolucion === venta.id_venta" class="spinner-border spinner-border-sm me-1"></span>
                  <i v-else class="bi bi-arrow-return-left me-1"></i>
                  Devolver
                </button>
                <span v-else class="text-muted small">No disponible</span>
              </td>
            </tr>
          </tbody>
        </table>
        <AppPagination :currentPage="ventasPagina" :totalPages="ventasTotalPaginas"
                       @page-changed="cambiarPaginaVentas" />
      </div>
    </div>

    <DevolucionModal
      v-if="ventaParaDevolver"
      :venta="ventaParaDevolver"
      @cerrado="ventaParaDevolver = null"
      @confirmada="onDevolucionConfirmada"
    />

    <ComprobanteEntrega
      :abierto="Boolean(ncAbierta)"
      :nota-credito-id="ncAbierta"
      @cerrado="ncAbierta = null"
    />
  </div>
</template>

<script>
import AppNavbar from '@/components/AppNavbar.vue'
import AppHeader from '@/components/AppHeader.vue'
import AppPagination from '@/components/AppPagination.vue'
import DevolucionModal from '@/components/DevolucionModal.vue'
import ComprobanteEntrega from '@/components/ComprobanteEntrega.vue'
import apiService from '@/services/apiService.js'
import { hasAnyPermission } from '@/services/authSessionService'
import { listarNotasCredito, ORIGEN_ETIQUETA } from '@/services/notasCreditoService'

const ESTADOS_DEVOLVIBLES = ['APPROVED', 'APPROVED_WITH_OBS']

export default {
  name: 'NotasCreditoView',
  components: { AppNavbar, AppHeader, AppPagination, DevolucionModal, ComprobanteEntrega },
  data () {
    return {
      ORIGEN_ETIQUETA,
      pestanaActiva: 'historial',
      cargando: false,

      notas: [],
      notasPagina: 1,
      notasTotalPaginas: 1,
      notasTotal: 0,
      filtro: '',
      origen: '',
      temporizador: null,
      ncAbierta: null,

      ventas: [],
      ventasPagina: 1,
      ventasPageSize: 10,
      ventasTotalPaginas: 1,
      ventasTotal: 0,
      abriendoDevolucion: null,
      ventaParaDevolver: null
    }
  },
  computed: {
    puedeDevolver () {
      return hasAnyPermission(['cobrar_venta'])
    }
  },
  mounted () {
    this.cargarNotas(1)
  },
  beforeUnmount () {
    clearTimeout(this.temporizador)
  },
  methods: {
    cambiarPestana (nombre) {
      this.pestanaActiva = nombre
      if (nombre === 'buscar' && !this.ventas.length) this.cargarVentas()
    },
    recargarPestanaActiva () {
      if (this.pestanaActiva === 'buscar') this.cargarVentas(this.ventasPagina)
      else this.cargarNotas(this.notasPagina)
    },
    buscarConDemora () {
      clearTimeout(this.temporizador)
      this.temporizador = setTimeout(() => this.cargarNotas(1), 300)
    },
    async cargarNotas (pagina = this.notasPagina) {
      this.cargando = true
      try {
        const data = await listarNotasCredito({ q: this.filtro, origen: this.origen, pagina, porPagina: 20 })
        this.notas = Array.isArray(data?.items) ? data.items : []
        this.notasPagina = Number(data?.pagina || pagina) || 1
        this.notasTotal = Number(data?.total || 0)
        this.notasTotalPaginas = Math.max(1, Math.ceil(this.notasTotal / (Number(data?.por_pagina) || 20)))
      } catch (error) {
        console.error('Error al cargar notas de crédito:', error)
        this.notas = []
        this.notasTotal = 0
      } finally {
        this.cargando = false
      }
    },
    async cargarVentas (pagina = this.ventasPagina) {
      this.cargando = true
      try {
        const { data } = await apiService.get('/api/sales', {
          pagina,
          pageSize: this.ventasPageSize,
          sortBy: 'nro_comprobante',
          sortDir: 'desc'
        })
        const payload = data?.data || {}
        this.ventas = Array.isArray(payload.items) ? payload.items : []
        this.ventasPagina = Number(payload.pagina || pagina) || 1
        this.ventasTotalPaginas = Number(payload.totalPages || 1) || 1
        this.ventasTotal = Number(payload.total || this.ventas.length) || this.ventas.length
      } catch (error) {
        console.error('Error al cargar ventas:', error)
        this.ventas = []
      } finally {
        this.cargando = false
      }
    },
    cambiarPaginaVentas (pagina) {
      if (pagina === this.ventasPagina) return
      this.cargarVentas(pagina)
    },
    esDevolvible (venta) {
      return ESTADOS_DEVOLVIBLES.includes(venta.fiscal_status)
    },
    async abrirDevolucion (idVenta) {
      this.abriendoDevolucion = idVenta
      try {
        const { data } = await apiService.get('/api/sales/detail', { id_venta: idVenta })
        const detalle = data?.data
        if (!detalle) throw new Error('No se obtuvo información de la venta.')
        this.ventaParaDevolver = detalle
      } catch (error) {
        console.error('Error al cargar detalle de venta para devolución:', error)
        alert(error?.response?.data?.message || error?.message || 'No se pudo cargar el detalle de la venta.')
      } finally {
        this.abriendoDevolucion = null
      }
    },
    onDevolucionConfirmada () {
      this.ventaParaDevolver = null
      this.cargarVentas(this.ventasPagina)
      // La NC de la devolución ya quedó registrada (RN-139): aparece en
      // «Emitidas» junto a las de las anulaciones.
      this.cargarNotas(1)
    },
    recortar (texto, largo = 48) {
      const t = String(texto || '')
      return t.length > largo ? `${t.slice(0, largo - 1)}…` : t
    },
    formatearMonto (valor) {
      const numero = Number(valor)
      if (!Number.isFinite(numero)) return '0'
      return Number(Math.round(numero)).toLocaleString('es-PY', { minimumFractionDigits: 0 })
    },
    formatearFecha (valor) {
      if (!valor) return '—'
      const fecha = new Date(valor)
      if (Number.isNaN(fecha.getTime())) return '—'
      const fechaStr = fecha.toLocaleDateString('es-PY')
      const horaStr = fecha.toLocaleTimeString('es-PY', { hour: '2-digit', minute: '2-digit' })
      return `${fechaStr} ${horaStr}`
    },
    fiscalBadgeClass (estado) {
      const normalized = (estado || '').toUpperCase()
      if (['APPROVED', 'APPROVED_WITH_OBS', 'EMITTED'].includes(normalized)) return 'bg-success'
      if (['QUEUED_BATCH', 'SENT_BATCH', 'DRAFT'].includes(normalized)) return 'bg-warning text-dark'
      if (['REJECTED', 'FAILED', 'TRANSMISSION_FAILED', 'FUNCTIONAL_FAILED'].includes(normalized)) return 'bg-danger'
      return 'bg-secondary'
    }
  }
}
</script>
